/* Where a sign-in lands.
 *
 * RoleBasedRoute sends a signed-out visitor to /portal/login with
 * `state.from` (the location they were trying to reach). Until 2026-09-21
 * PortalLogin ignored it and always sent a customer to the dashboard, so an
 * emailed link to /portal/customer/payments worked ONLY for a parent who was
 * already signed in, and silently lost its destination for the parent most
 * likely to be clicking it from an email. Found when Lisa Monjeau could not
 * find where to add a payment method.
 *
 * Two rules, both deliberate:
 *   - The destination must be an INTERNAL portal path for the role that just
 *     signed in. A customer is never bounced into /portal/work, staff are never
 *     bounced into a family page, and nothing here will ever navigate to a
 *     string that is not a same-origin path (no "//evil", no "https://").
 *   - The destination is stashed in sessionStorage too, because the Google
 *     sign-in on mobile / the installed PWA uses signInWithRedirect: the page
 *     is left entirely, and router state does not survive that round trip.
 */

const STASH_KEY = 'cyv.portal.postLogin';

const STAFF_HOME = '/portal';
const CUSTOMER_HOME = '/portal/customer';

function isStaff(role) {
	return role === 'employee' || role === 'admin';
}

/* Pathname (+ search + hash) from a router location object or a plain string.
 * Returns null for anything that is not a plain same-origin path. */
function pathFrom(from) {
	if (!from) return null;
	let path = null;
	if (typeof from === 'string') path = from;
	else if (typeof from.pathname === 'string') {
		path = from.pathname + (from.search || '') + (from.hash || '');
	}
	if (!path || path[0] !== '/' || path[1] === '/' || path[1] === '\\') return null;
	return path;
}

/* Pure: the path to navigate to after sign-in. */
export function resolvePostLoginPath(role, from) {
	const home = isStaff(role) ? STAFF_HOME : CUSTOMER_HOME;
	const path = pathFrom(from);
	if (!path) return home;
	if (path === '/portal/login' || path.startsWith('/portal/login?')) return home;
	if (isStaff(role)) {
		// Staff may land anywhere under /portal that is not a family page.
		if (path === '/portal' || path.startsWith('/portal/')) {
			return path.startsWith(CUSTOMER_HOME) ? home : path;
		}
		return home;
	}
	// A customer may land only inside the customer portal.
	if (path === CUSTOMER_HOME || path.startsWith(CUSTOMER_HOME + '/')) return path;
	return home;
}

export function stashPostLoginPath(from) {
	const path = pathFrom(from);
	if (!path) return;
	try {
		window.sessionStorage.setItem(STASH_KEY, path);
	} catch (_) {
		/* private mode / blocked storage: the router state still works */
	}
}

/* Reads AND clears the stash, so a stale destination cannot resurface on a
 * later, unrelated sign-in. */
export function takeStashedPostLoginPath() {
	try {
		const v = window.sessionStorage.getItem(STASH_KEY);
		if (v != null) window.sessionStorage.removeItem(STASH_KEY);
		return v;
	} catch (_) {
		return null;
	}
}
